import type {
  CustomerDocument,
  DealDocActivityEvent,
  DealDocActivityGroup,
  DocumentChunkStatus,
} from '@protoss/shared';
import {
  ChevronDownIcon,
  FileIcon,
  Pill,
  ReloadIcon,
  RichTextEditor,
  SkeletonBlock,
} from '@protoss/ui';
import * as React from 'react';
import { formatRelativeTime } from '../../../lib/datetime';
import { parseContentToTiptap } from '../../../lib/tiptap';
import { salesApi } from '../services/salesApi';

// ─────────────────────────────────────────────────────────────────────────────
// Shared helpers
// ─────────────────────────────────────────────────────────────────────────────

function isDocumentProcessing(chunkStatus: DocumentChunkStatus | null | undefined): boolean {
  return chunkStatus === 'pending';
}

function DocumentProcessingStatus() {
  return (
    <span className="customer-docs-panel__processing-status">
      <ReloadIcon
        width={12}
        height={12}
        className="customer-docs-panel__processing-spinner"
        aria-hidden="true"
      />
      <span>processing...</span>
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-document processing activity (badges + expandable change detail)
// ─────────────────────────────────────────────────────────────────────────────

const EVENT_KIND: Record<
  DealDocActivityEvent['eventType'],
  { label: string; variant: 'success' | 'accent' | 'danger' | 'muted' } | null
> = {
  doc_processed: null, // summary row - counts only, never a detail line
  question_added: { label: 'Added', variant: 'success' },
  question_updated: { label: 'Updated', variant: 'accent' },
  question_deleted: { label: 'Deleted', variant: 'danger' },
  question_merged: { label: 'Merged', variant: 'muted' },
};

function DocActivityBadges({ counts }: { counts: DealDocActivityGroup['counts'] }) {
  return (
    <>
      {counts.added > 0 ? <Pill variant="success">+{counts.added} new</Pill> : null}
      {counts.updated > 0 ? <Pill variant="accent">{counts.updated} updated</Pill> : null}
      {counts.deleted > 0 ? <Pill variant="danger">{counts.deleted} removed</Pill> : null}
    </>
  );
}

function DocActivityEventRow({ event }: { event: DealDocActivityEvent }) {
  const kind = EVENT_KIND[event.eventType];
  if (!kind) return null;
  const previousValue =
    typeof (event.detail as { previousValue?: unknown } | null)?.previousValue === 'string'
      ? ((event.detail as { previousValue: string }).previousValue)
      : null;
  const mergedInto =
    typeof (event.detail as { mergedIntoLabel?: unknown } | null)?.mergedIntoLabel === 'string'
      ? ((event.detail as { mergedIntoLabel: string }).mergedIntoLabel)
      : null;
  return (
    <div className="customer-docs-panel__activity-event">
      <Pill variant={kind.variant}>{kind.label}</Pill>
      <span className="customer-docs-panel__activity-event-label">
        {event.subjectLabel ?? event.subjectFieldKey ?? '(question)'}
        {mergedInto ? ` -> ${mergedInto}` : ''}
      </span>
      {previousValue ? (
        <span className="customer-docs-panel__activity-prev" title={previousValue}>
          was: {previousValue}
        </span>
      ) : null}
      {event.actor === 'user' ? <Pill variant="ghost">You</Pill> : null}
      <span className="customer-docs-panel__activity-time">
        {formatRelativeTime(event.occurredAt)}
      </span>
    </div>
  );
}

/** Badges + chevron toggle (sits in the row line; the detail renders below the row). */
function DocActivityToggle({
  detailId,
  counts,
  expanded,
  onToggle,
}: {
  detailId: string;
  counts: DealDocActivityGroup['counts'];
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      className="customer-docs-panel__activity-toggle"
      aria-expanded={expanded}
      aria-controls={detailId}
      onClick={onToggle}
    >
      <DocActivityBadges counts={counts} />
      <ChevronDownIcon className="customer-docs-panel__activity-chevron" aria-hidden="true" />
    </button>
  );
}

function DocActivityDetail({ id, events }: { id: string; events: DealDocActivityEvent[] }) {
  return (
    <div id={id} className="customer-docs-panel__activity-detail">
      {events.map((e) => (
        <DocActivityEventRow key={e.uuid} event={e} />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AI doc item type
// ─────────────────────────────────────────────────────────────────────────────

export interface AiDocItem {
  kind: 'estimate' | 'resource-plan' | 'sow';
  label: string;
  /** ISO timestamp of last generation, or null if not yet generated. */
  generatedAt: string | null;
  /** Generation status while the artifact is being (re)generated. */
  docStatus?: 'queued' | 'generating';
  onView: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// DealDocumentsPanel - list view
// ─────────────────────────────────────────────────────────────────────────────

interface DealDocumentsPanelProps {
  documents: CustomerDocument[];
  isLoading: boolean;
  aiDocs: AiDocItem[];
  uploadedOnly?: boolean;
  onViewDocument: (docUuid: string) => void;
  /** Per-document processing history (badges + expandable change detail). Absent = no badges. */
  activityByDoc?: Record<string, DealDocActivityGroup>;
  /** Doc-less user events (deletes/merges) - rendered as a trailing "Manual edits" row. */
  userEvents?: DealDocActivityEvent[];
}

export function DealDocumentsPanel({
  documents,
  isLoading,
  aiDocs,
  uploadedOnly,
  onViewDocument,
  activityByDoc,
  userEvents,
}: DealDocumentsPanelProps) {
  // Which rows have their change detail expanded (ephemeral - the panel remounts per tab visit).
  const [expandedDocs, setExpandedDocs] = React.useState<Set<string>>(new Set());
  const toggleExpanded = (key: string) =>
    setExpandedDocs((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  if (isLoading && documents.length === 0) {
    return (
      <div className="attr-descriptions-panel">
        <SkeletonBlock className="attr-descriptions-panel__skeleton" />
        <SkeletonBlock className="attr-descriptions-panel__skeleton" />
        <SkeletonBlock className="attr-descriptions-panel__skeleton" />
      </div>
    );
  }

  return (
    <div className="customer-docs-panel">
      {/* Section 1: AI-generated documents (hidden if uploadedOnly) */}
      {!uploadedOnly && (
        <>
          <div className="customer-docs-panel__section-header">AI Generated</div>
          <ul className="customer-docs-panel__list" role="list">
            {aiDocs.map((doc) => {
              const isDisabled = !doc.generatedAt || !!doc.docStatus;
              const statusText =
                doc.docStatus === 'generating'
                  ? 'Generating\u2026'
                  : doc.docStatus === 'queued'
                    ? 'Queued'
                    : doc.generatedAt
                      ? formatRelativeTime(doc.generatedAt)
                      : 'Not generated';
              return (
                <li key={doc.kind} className="customer-docs-panel__row">
                  <button
                    type="button"
                    className={`customer-docs-panel__row-body${isDisabled ? ' customer-docs-panel__row-body--disabled' : ''}`}
                    onClick={isDisabled ? undefined : doc.onView}
                    disabled={isDisabled}
                  >
                    <span className="customer-docs-panel__label">{doc.label}</span>
                    <span className="customer-docs-panel__status">{statusText}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {/* Section 2: User-uploaded source documents (transcripts, issue logs, status reports, ...) */}
      <div className="customer-docs-panel__section-header">Uploaded documents</div>
      {documents.length === 0 ? (
        <p className="customer-docs-panel__section-empty">No documents attached to this deal.</p>
      ) : (
        <ul className="customer-docs-panel__list" role="list">
          {documents.map((doc: CustomerDocument) => {
            const activity = activityByDoc?.[doc.uuid];
            const hasActivity =
              !!activity &&
              activity.counts.added + activity.counts.updated + activity.counts.deleted > 0;
            const expanded = expandedDocs.has(doc.uuid);
            return (
              <li key={doc.uuid} className="customer-docs-panel__row">
                {/* The open button + the activity toggle are SIBLINGS (a nested button is invalid
                    HTML), sharing one row line. */}
                <div className="customer-docs-panel__row-line">
                  <button
                    type="button"
                    className="customer-docs-panel__row-body"
                    onClick={() => onViewDocument(doc.uuid)}
                  >
                    <span className="customer-docs-panel__label customer-docs-panel__label--truncate">
                      {doc.title}
                    </span>
                    <span className="customer-docs-panel__status">
                      {formatRelativeTime(doc.updatedAt)}
                      {isDocumentProcessing(doc.chunkStatus) && <DocumentProcessingStatus />}
                    </span>
                  </button>
                  {hasActivity ? (
                    <DocActivityToggle
                      detailId={`doc-activity-${doc.uuid}`}
                      counts={activity.counts}
                      expanded={expanded}
                      onToggle={() => toggleExpanded(doc.uuid)}
                    />
                  ) : null}
                </div>
                {hasActivity && expanded ? (
                  <DocActivityDetail id={`doc-activity-${doc.uuid}`} events={activity.events} />
                ) : null}
              </li>
            );
          })}
          {/* Doc-less user events (question deletes/merges) - the change history's trailing row. */}
          {userEvents && userEvents.length > 0 ? (
            <li className="customer-docs-panel__row customer-docs-panel__row--user-activity">
              <div className="customer-docs-panel__row-line">
                <span className="customer-docs-panel__row-body customer-docs-panel__row-body--static">
                  <span className="customer-docs-panel__label">Manual edits</span>
                  <span className="customer-docs-panel__status">
                    {formatRelativeTime(userEvents[0]!.occurredAt)}
                  </span>
                </span>
                <DocActivityToggle
                  detailId="doc-activity-user"
                  counts={{
                    added: 0,
                    updated: 0,
                    deleted: userEvents.filter(
                      (e) => e.eventType === 'question_deleted' || e.eventType === 'question_merged'
                    ).length,
                  }}
                  expanded={expandedDocs.has('__user__')}
                  onToggle={() => toggleExpanded('__user__')}
                />
              </div>
              {expandedDocs.has('__user__') ? (
                <DocActivityDetail id="doc-activity-user" events={userEvents} />
              ) : null}
            </li>
          ) : null}
        </ul>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DealDocumentViewPanel - detail view
// ─────────────────────────────────────────────────────────────────────────────

interface DealDocumentViewPanelProps {
  dealUuid: string;
  docUuid: string;
}

export function DealDocumentViewPanel({ dealUuid, docUuid }: DealDocumentViewPanelProps) {
  const [doc, setDoc] = React.useState<{
    title: string;
    updatedAt: string;
    chunkStatus: DocumentChunkStatus | null;
    content: Record<string, unknown>;
  } | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    salesApi
      .getDealDocument(dealUuid, docUuid)
      .then(({ document: d }) => {
        if (cancelled) return;
        setDoc({
          title: d.title,
          updatedAt: d.updatedAt,
          chunkStatus: d.chunkStatus,
          content: parseContentToTiptap(d.contentText),
        });
      })
      .catch((err) => console.error('[DealDocumentsPanel] failed to load document', err))
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dealUuid, docUuid]);

  if (isLoading) {
    return (
      <div className="customer-doc-view-panel">
        <div className="customer-doc-view-panel__header">
          <div className="customer-doc-view-panel__title-skeleton">
            <SkeletonBlock />
          </div>
          <div className="customer-doc-view-panel__meta-skeleton">
            <SkeletonBlock />
          </div>
        </div>
        <div className="customer-doc-view-panel__body">
          <div className="customer-doc-view-panel__body-skeleton">
            <SkeletonBlock />
          </div>
        </div>
      </div>
    );
  }

  if (!doc) return null;

  return (
    <div className="customer-doc-view-panel">
      <div className="customer-doc-view-panel__header">
        <div className="customer-doc-view-panel__title-row">
          <h3 className="customer-doc-view-panel__title">
            <span className="customer-doc-view-panel__title-text">{doc.title}</span>
            <span className="customer-docs-panel__kind-icon" aria-hidden="true">
              <FileIcon width={12} height={12} />
            </span>
          </h3>
        </div>
        <span className="customer-doc-view-panel__meta">
          {formatRelativeTime(doc.updatedAt)}
          {isDocumentProcessing(doc.chunkStatus) && <DocumentProcessingStatus />}
        </span>
      </div>
      <div className="customer-doc-view-panel__body">
        <RichTextEditor content={doc.content} readOnly />
      </div>
    </div>
  );
}
